import "./DSAManage.scss";
import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Carousel, Checkbox, Drawer, Dropdown, Input, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DownOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { ScreenSetUpModal } from "../dsa/ScreenSetUpModal";
import { DesignView, EditScreenContentModal } from "../dsa/EditScreenContentModal";
import { AppContext } from "../../AppContext";
import { Spinner } from "../account/misc/Spinner";
import { DsaBoardDto, DsaBoardDtoTypeEnum, DsaContentDtoTypeEnum, DsaPlaylistDto, DsaPlaylistEntryDto, DsaScheduleDto, DsaScreenDto } from "../../generated/dashboard-client";
import DSAApi from "../../api/DSAApi";
import { handleServerError } from "../../util/ErrorHandler";
import { useRecoilState, useRecoilValue } from "recoil";
import { locationsState, memberState, mobileState } from "../../state/atoms";
import { AxiosResponse } from "axios";
import { ScreenSetUpHelpModal } from "../dsa/ScreenSetUpHelpModal";
import { ManageScreenModal } from "../dsa/ManageScreenModal";
import PreviewModal from "../dsa/PreviewModal";
import { RebootScreenModal } from "../dsa/RebootScreenModal";
import { RemoveScreenModal } from "../dsa/RemoveScreenModal";
import ScreenCard from "../dsa/ScreenCard";
import { DisplayDiagnosticsOnScreenConfirmationModal } from "../dsa/DisplayDiagnosticsOnScreenConfirmationModal";
import { useOnClickOutside } from "usehooks-ts";
import { DeactivateScreenModal } from "../dsa/DeactivateScreenModal";
import AvailableScreenCard from "../dsa/AvailableScreenCard";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import StartPlayingIcon from "../icons/StartPlayingIcon";
import SortScreenIcon from "../icons/SortScreenIcon";
import CloseCircle from "../icons/CloseCircle";
import WiFiSignalOff from "../icons/WiFiSignalOff";
import { isDesignSubscription, isProSubscription } from "../../service/BillingService";

enum SortType { none, screenNameAscending, screenNameDescending, locNameAscending, locNameDescending, contentTypeAscending, contentTypeDescending };
enum AvailabilityType { all, active, new, deactivated };

export interface ScreenView extends DsaScreenDto {
  visible?: boolean;
  selected?: boolean;
}

export default function DSAManage() {
  const { location } = useContext(AppContext);
  const [locations] = useRecoilState(locationsState);
  const member = useRecoilValue(memberState);
  const mobile = useRecoilValue(mobileState);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [availableDevicesLoading, setAvailableDevicesLoading] = useState(true);
  const [deactivatedDevicesLoading, setDeactivatedDevicesLoading] = useState(true);
  const [reactivating, setReactivating] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [availableDevices, setAvailableDevices] = useState(0);
  const [deactivatedDevices, setDeactivatedDevices] = useState(0);
  const [screens, setScreens] = useState([] as ScreenView[]);
  const [screensFetched, setScreensFetched] = useState(false);
  const [totalScreensAndLicenses, setTotalScreensAndLicenses] = useState(0);
  const [active, setActive] = useState(undefined as ScreenView | undefined);
  const [activeNumber, setActiveNumber] = useState(0);
  const [search, setSearch] = useState("");
  const [boards, setBoards] = useState([] as DsaBoardDto[]);
  const [playlists, setPlaylists] = useState([] as DsaPlaylistDto[]);
  const [schedules, setSchedules] = useState([] as DsaScheduleDto[]);
  const [uploads, setUploads] = useState([] as DisplayUploadDto[]);

  const [isSetUpModal, setSetUpModal] = useState(false);
  const [isSetUpHelpModal, setSetUpHelpModal] = useState(false);
  const [isEditContentModal, setEditContentModal] = useState(false);
  const [isManageScreenModal, setManageScreenModal] = useState(false);
  const [isRebootScreenModal, setRebootScreenModal] = useState(false);
  const [isDiagnosticsScreenModal, setDiagnosticsScreenModal] = useState(false);
  const [isRemoveScreenModal, setRemoveScreenModal] = useState(false);
  const [isDeactivateScreenModal, setDeactivateScreenModal] = useState(false);

  const [isPreviewModal, setPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState('');
  const [previewBoard, setPreviewBoard] = useState({} as DsaBoardDto);
  const [previewEntries, setPreviewEntries] = useState([] as DsaPlaylistEntryDto[]);
  const [previewUploadId, setPreviewUploadId] = useState('');

  const [sortType, setSortType] = useState(SortType.none);
  const [isFilterMenuVisible, setFilterMenuVisible] = useState(false);
  const filterMenuRef = useRef(null);
  const [filteredLocs, setFilteredLocs] = useState([] as number[]);
  const [filteredStatuses, setFilteredStatuses] = useState([] as string[]);
  const [filteredContentTypes, setFilteredContentTypes] = useState([] as DsaContentDtoTypeEnum[]);
  const [numFilters, setNumFilters] = useState(0);
  const [screenAvailabilityFilter, setScreenAvailabilityFilter] = useState(AvailabilityType.all);

  const [isMobileSortMenuVisible, setMobileSortMenuVisible] = useState(false);
  const [isMobileFilterMenuVisibile, setMobileFilterMenuVisible] = useState(false);

  const fetchDevices = useCallback(() => {
    if (!locations.length) return;
    if (screensFetched) return;
    setDevicesLoading(true);
    DSAApi.getScreens().then(r => {
      let screens = r.data as ScreenView[];
      const locIds = locations.map(loc => loc.internalId);
      screens = screens.filter(sc => { return locIds.includes(sc.locationId) });
      screens.forEach(sc => sc.visible = true);
      setDevicesLoading(false);
      setScreensFetched(true);
      setScreens([...screens]);
    }).catch(e => {
      handleServerError(e, "Failed to fetch devices.");
    });
  }, [locations, screensFetched]);

  const fetchAvailableDevices = useCallback(() => {
    setAvailableDevicesLoading(true);
    DSAApi.getScreensTotal().then(r => {
      setAvailableDevicesLoading(false);
      setAvailableDevices(r.data);
    }).catch(e => {
      handleServerError(e, "Failed to fetch available devices.");
    });
  }, []);

  const fetchDeactivatedDevices = useCallback(() => {
    setDeactivatedDevicesLoading(true);
    DSAApi.getDeactivatedScreensTotal().then(r => {
      setDeactivatedDevicesLoading(false);
      setDeactivatedDevices(r.data);
    }).catch(e => {
      handleServerError(e, "Failed to fetch deactivated devices.");
    });
  }, []);

  useEffect(fetchDevices, [fetchDevices]);
  useEffect(fetchAvailableDevices, [fetchAvailableDevices]);
  useEffect(fetchDeactivatedDevices, [fetchDeactivatedDevices]);

  useEffect(() => {
    setTotalScreensAndLicenses(screens.length + availableDevices + deactivatedDevices);
  }, [screens.length, availableDevices, deactivatedDevices])

  useEffect(() => {
    setLoading(devicesLoading || availableDevicesLoading || deactivatedDevicesLoading);
  }, [devicesLoading, availableDevicesLoading, deactivatedDevicesLoading])

  useEffect(() => {
    if (!loading) {
      if (totalScreensAndLicenses === 0) {
        if (isProSubscription(member)) {
          window.MHM.ProModal.addDSA({
            memberId: `${member.ownerId}` || member.id, onSuccess: () => {
              window.location.reload();
            }
          });
        }
      }
    }
  }, [loading, member, totalScreensAndLicenses])

  useEffect(() => {
    Promise.all(
      screens
        .filter(it => it.content?.type === 'BOARD')
        .map(it => DSAApi.getBoard(it.content?.sourceId || ''))
    ).then((value: AxiosResponse<DsaBoardDto>[]) => {
      setBoards(value.map(it => it.data));
    });
  }, [screens]);

  useEffect(() => {
    Promise.all(
      screens
        .filter(it => it.content?.type === 'PLAYLIST')
        .map(it => DSAApi.getPlaylist(it.content?.sourceId || ''))
    ).then((value: AxiosResponse<DsaBoardDto>[]) => {
      setPlaylists(value.map(it => it.data));
    });
  }, [screens]);

  useEffect(() => {
    DSAApi.getSchedules().then(resp => {
      setSchedules(resp.data);
    });
  }, []);

  useEffect(() => {
    QRDisplayUploadsApi.getDisplayUploadsByLocationIds(locations.map(l => l.internalId!)).then(resp => {
      setUploads(resp.data);
    });
  }, [locations]);

  function onUpgradeClick() {
    if (isDesignSubscription(member)) {
      window.MHM.ProModal.switchToProWithOverview({
        memberId: member.id,
        onSuccess: () => {
          sessionStorage.setItem('load-after-upgrade', 'true');
          window.location.reload();
        }
      });
    } else {
      window.MHM.ProModal.upgrade({
        memberId: member.id,
        onSuccess: () => {
          sessionStorage.setItem('load-after-upgrade', 'true');
          window.location.reload();
        }
      });
    }
  }

  useEffect(() => {
    if (sessionStorage.getItem('load-after-upgrade')) {
      sessionStorage.removeItem('load-after-upgrade');
      onOrderDevice();
    }
  })

  useEffect(() => {
    if (sessionStorage.getItem('setup-after-locadd')) {
      setSetUpModal(true);
    }
  }, [setSetUpModal])

  async function onOrderDevice() {
    if (!isProSubscription(member)) {
      if (isDesignSubscription(member)) {
        window.MHM.ProModal.switchToProWithOverview({ memberId: member.id, onSuccess: () => window.location.reload() });
      } else {
        window.MHM.ProModal.upgrade({ memberId: member.id, onSuccess: () => window.location.reload() });
      }
      return;
    }

    window.MHM.ProModal.addDSA({
      memberId: `${member.ownerId}` || member.id, onSuccess: () => {
        window.location.reload();
      }
    });
  }

  function onScreenAdded(screen: DsaScreenDto) {
    setScreens([{ ...screen, visible: true }, ...screens]);
    setSetUpModal(false);
    setScreensFetched(false);
    fetchDevices();
    fetchAvailableDevices();
  }

  function onScreenUpdated(screen: DsaScreenDto, timezone: string, rotation: number, scheduledReboot: boolean, restartDay: number, restartTime: string) {
    const thisScreen = screens.find(it => it.serial === screen.serial);
    if (thisScreen) {
      thisScreen.name = screen.name;
      thisScreen.locationId = screen.locationId;
      thisScreen.placement = screen.placement;
      thisScreen.timezone = timezone;
      thisScreen.rotation = rotation;
      thisScreen.scheduledReboot = scheduledReboot;
      if (scheduledReboot) {
        thisScreen.restartDay = restartDay;
        thisScreen.restartTime = restartTime;
      } else {
        thisScreen.restartDay = 0;
        thisScreen.restartTime = '';
      }
      setScreens([...screens]);
    }
    onCloseManageScreenModal();
  }

  function onCloseManageScreenModal() {
    setActive({});
    setManageScreenModal(false);
  }

  async function onPlaylistSelect(playlist: DsaPlaylistDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "PLAYLIST",
        sourceId: playlist.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }
  async function onScheduleSelect(schedule: DsaScheduleDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "SCHEDULE",
        sourceId: schedule.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }

  async function onDesignSelect(design: DesignView) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const board = await DSAApi.createBoard({
        type: DsaBoardDtoTypeEnum.Design,
        designId: design.id,
        page: design.page,
        name: design.name,
        memberId: member.internalId,
        locationId: location.internalId
      });

      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "BOARD",
        sourceId: board.data.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }

  async function onUploadSelect(upload: DisplayUploadDto) {
    setEditContentModal(false);
    try {
      setLoadingContent(true);
      const board = await DSAApi.createBoard({
        type: DsaBoardDtoTypeEnum.Upload,
        name: upload.name,
        memberId: member.internalId,
        locationId: location.internalId,
        uploadId: upload.id
      });

      const content = await DSAApi.setScreenContent(active?.serial!!, {
        type: "BOARD",
        sourceId: board.data.id,
        playable: true
      });

      setLoadingContent(false);
      setScreens([{ ...content.data, visible: true }, ...screens.filter(it => it.serial !== content.data.serial)]);
    } catch (e) {
      setLoadingContent(false);
      handleServerError(e);
    }
  }


  function onSearchChange(search: string) {
    setSearch(search);
    screens.forEach(sc => {
      const scName = sc.name!.toLowerCase();
      const scLoc = locations.find(loc => loc.internalId === sc.locationId)!.name!.toLowerCase();
      const c = sc.content!;
      let scContentName = '';
      if (c.type === 'BOARD') {
        scContentName = boards.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      } else if (c.type === 'PLAYLIST') {
        scContentName = playlists.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      } else if (c.type === 'SCHEDULE') {
        scContentName = schedules.find(it => it.id === c.sourceId)?.name?.toLowerCase() || '';
      }
      sc.visible = scName.includes(search.toLowerCase()) || scLoc!.includes(search.toLowerCase()) || scContentName.includes(search.toLowerCase());
    })
    setScreens([...screens]);
  }

  function lockedForUser(screen: DsaScreenDto) {
    return !member.owner && screen.locked;
  }

  function openManageScreenModal(screen: DsaScreenDto) {
    setActive(screen);
    setManageScreenModal(true);
  }

  function openPreviewModal(screen: DsaScreenDto) {
    setPreviewModal(true);
    const content = screen.content!;
    if (content.type === 'BOARD') {
      setPreviewEntries([]);
      const previewBoard = boards.find(b => b.id === content.sourceId)!;
      if (previewBoard.type === 'UPLOAD') {
        setPreviewType('upload');
        setPreviewUploadId(previewBoard.uploadId!);
        setPreviewBoard({});
      } else {
        setPreviewType('board');
        setPreviewBoard(previewBoard);
        setPreviewUploadId('');
      }
    } else if (content.type === 'PLAYLIST') {
      setPreviewType('playlist');
      setPreviewEntries(playlists.find(b => b.id === content.sourceId)!.entries!.sort((a, b) => { return a.sequence! - b.sequence! }));
      setPreviewBoard({});
      setPreviewUploadId('');
    }
  }

  function rebootScreen(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;
    setActive(screen);
    setRebootScreenModal(true);
  }

  function showDiagnostics(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;
    setActive(screen);
    setDiagnosticsScreenModal(true);
  }

  function removeScreen(screen: DsaScreenDto) {
    if (lockedForUser(screen)) return;
    setActive(screen);
    setRemoveScreenModal(true);
  }

  function onCloseRebootScreenModal(fetch: boolean) {
    setRebootScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
    }
  }

  function onCloseDiagnosticsScreenModal(fetch: boolean) {
    setDiagnosticsScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
    }
  }

  function onCloseRemoveScreenModal(fetch: boolean) {
    setRemoveScreenModal(false);
    if (fetch) {
      setScreensFetched(false);
      fetchDevices();
      fetchAvailableDevices();
    }
  }

  function deactivateScreen(it: number) {
    setActiveNumber(it);
    setDeactivateScreenModal(true);
  }

  function onCloseDeactivateScreenModal(fetch: boolean) {
    setDeactivateScreenModal(false);
    if (fetch) {
      fetchAvailableDevices();
      fetchDeactivatedDevices();
    }
  }

  function reactivateScreenLicense() {
    setReactivating(true);
    window.MHM.ProModal.reactivateDSALicense({ memberId: member.id! }).then(() => {
      fetchAvailableDevices();
      fetchDeactivatedDevices();
      setReactivating(false);
    });
  }

  function withTooltip(tooltip: string, object: ReactNode) {
    return <Tooltip placement="bottom" title={tooltip} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
      {object}
    </Tooltip>
  }

  function triggerOpenMenu(it: DsaScreenDto) {
    document.getElementById(it.serial!)?.click();
  }

  function getLocNameFromId(locId: number) {
    return locations.find(loc => loc.internalId === locId)!.name!;
  }

  function onLocSortClick() {
    if (sortType === SortType.locNameAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.locNameDescending);
    } else {
      screens.sort((a, b) => getLocNameFromId(a.locationId!).toLowerCase().localeCompare(getLocNameFromId(b.locationId!).toLowerCase()));
      setScreens([...screens]);
      setSortType(SortType.locNameAscending);
    }
  }

  function onScreenNameSortClick() {
    if (sortType === SortType.screenNameAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.screenNameDescending);
    } else {
      screens.sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()));
      setScreens([...screens]);
      setSortType(SortType.screenNameAscending);
    }
  }

  function onContentTypeSortClick() {
    if (sortType === SortType.contentTypeAscending) {
      setScreens([...screens.reverse()]);
      setSortType(SortType.contentTypeDescending);
    } else {
      screens.sort((a, b) => a.content!.type!.localeCompare(b.content!.type!));
      setScreens([...screens]);
      setSortType(SortType.contentTypeAscending);
    }
  }

  function onSortMobile(sort: SortType) {
    if (sort === SortType.screenNameAscending || sort === SortType.screenNameDescending) {
      screens.sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()));
      if (sort === SortType.screenNameDescending) {
        screens.reverse();
      }
    } else if (sort === SortType.locNameAscending || sort === SortType.locNameDescending) {
      screens.sort((a, b) => getLocNameFromId(a.locationId!).toLowerCase().localeCompare(getLocNameFromId(b.locationId!).toLowerCase()));
      if (sort === SortType.locNameDescending) {
        screens.reverse();
      }
    }
    setScreens([...screens]);
    setSortType(sort);
    setMobileSortMenuVisible(false);
  }

  const handleFilterClickOutside = (ev: MouseEvent) => {
    setFilterMenuVisible(false);
  }

  useOnClickOutside(filterMenuRef, (ev) => handleFilterClickOutside(ev as MouseEvent));

  function toggleFilterLoc(locId: number, checked: boolean) {
    if (checked) {
      setFilteredLocs([...filteredLocs, locId]);
    } else {
      filteredLocs.splice(filteredLocs.indexOf(locId), 1);
      setFilteredLocs([...filteredLocs]);
    }
  }

  function toggleFilterStatus(status: string, checked: boolean) {
    if (checked) {
      setFilteredStatuses([...filteredStatuses, status]);
    } else {
      filteredStatuses.splice(filteredStatuses.indexOf(status), 1);
      setFilteredStatuses([...filteredStatuses]);
    }
  }

  function toggleFilterContentType(contentType: DsaContentDtoTypeEnum, checked: boolean) {
    if (checked) {
      setFilteredContentTypes([...filteredContentTypes, contentType]);
    } else {
      filteredContentTypes.splice(filteredContentTypes.indexOf(contentType), 1);
      setFilteredContentTypes([...filteredContentTypes]);
    }
  }

  function onFilterClick() {
    if (filteredLocs.length || filteredStatuses.length || filteredContentTypes.length) {
      screens.forEach(sc => {
        sc.visible = true;
      });

      if (filteredLocs.length) {
        screens.forEach(sc => {
          sc.visible = filteredLocs.includes(sc.locationId!);
        })
      }
      if (filteredStatuses.length) {
        screens.forEach(sc => {
          const scStatus = sc.online ? 'online' : 'offline';
          if (sc.visible) {
            sc.visible = filteredStatuses.includes(scStatus);
          }
        })
      }
      if (filteredContentTypes.length) {
        screens.forEach(sc => {
          if (sc.visible) {
            sc.visible = sc.content && filteredContentTypes.includes(sc.content.type!);
          }
        })
      }
      setScreens([...screens]);
      setFilterMenuVisible(false);
      setMobileFilterMenuVisible(false);
      setNumFilters(filteredLocs.length + filteredStatuses.length + filteredContentTypes.length);
    }
  }

  function onFilterClearClick() {
    setFilteredLocs([]);
    setFilteredStatuses([]);
    setFilteredContentTypes([]);
    screens.forEach(sc => {
      sc.visible = true;
    })
    setScreens([...screens]);
    setFilterMenuVisible(false);
    setMobileFilterMenuVisible(false);
    setNumFilters(0);
  }

  const doubleDash = <>&mdash;&nbsp;&mdash;</>;

  const filterMenuList = <ul>
    <div className="label">Location</div>
    {locations.map(loc => {
      return <li key={loc.id}>
        <Checkbox checked={filteredLocs.includes(loc.internalId!)}
          onChange={(ev) => toggleFilterLoc(loc.internalId!, ev.target.checked)} />
        <span>{loc.name!}</span>
      </li>
    })}

    <div className="separator" />

    <div className="label">Status</div>
    <li><Checkbox checked={filteredStatuses.includes('online')} onChange={(ev) => toggleFilterStatus('online', ev.target.checked)} />Online
    </li>
    <li><Checkbox checked={filteredStatuses.includes('offline')} onChange={(ev) => toggleFilterStatus('offline', ev.target.checked)} />Offline
    </li>
    <li><Checkbox checked={filteredStatuses.includes('deactivated')} onChange={(ev) => toggleFilterStatus('deactivated', ev.target.checked)} />Deactivated
    </li>

    <div className="separator" />

    <div className="label">Content Type</div>
    <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoTypeEnum.Board)}
      onChange={(ev) => toggleFilterContentType(DsaContentDtoTypeEnum.Board, ev.target.checked)} />Design
    </li>
    <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoTypeEnum.Playlist)}
      onChange={(ev) => toggleFilterContentType(DsaContentDtoTypeEnum.Playlist, ev.target.checked)} />Playlist
    </li>
    <li><Checkbox checked={filteredContentTypes.includes(DsaContentDtoTypeEnum.Schedule)}
      onChange={(ev) => toggleFilterContentType(DsaContentDtoTypeEnum.Schedule, ev.target.checked)} />Schedule
    </li>
  </ul>;

  const filterFooter = <div className="filter-footer">
    <Button className={`clear-btn`} onClick={() => onFilterClearClick()}>Clear</Button>
    <Button className={!filteredLocs.length && !filteredStatuses.length && !filteredContentTypes.length ? 'disabled' : ''}
      type="primary" onClick={() => onFilterClick()}>Filter</Button>
  </div>

  return <div>
    {loading && <div className={'dsa'}>
      <div className={'spinner'}><Spinner /></div>
    </div>}

    {!loading && <section className={"dsa"}>
      {isProSubscription(member) && totalScreensAndLicenses > 0 &&
        <header>
          {mobile && screens.length > 0 &&
            <Input type="search" prefix={<SearchOutlined />} placeholder="Search Screens" value={search}
              onChange={ev => onSearchChange(ev.target.value)} />}
          {mobile && (screens.length > 0 || !!availableDevices) &&
            <Button className="order-screen" type="primary" onClick={() => onOrderDevice()}>Order Screens</Button>}

          <div className="availability-filters">
            <ul>
              <li className={screenAvailabilityFilter === AvailabilityType.all ? 'active' : ''} onClick={() => setScreenAvailabilityFilter(AvailabilityType.all)}>All</li>
              <li className={screenAvailabilityFilter === AvailabilityType.active ? 'active' : ''} onClick={() => setScreenAvailabilityFilter(AvailabilityType.active)}>Active</li>
              <li className={screenAvailabilityFilter === AvailabilityType.new ? 'active' : ''} onClick={() => setScreenAvailabilityFilter(AvailabilityType.new)}>New</li>
              <li className={screenAvailabilityFilter === AvailabilityType.deactivated ? 'active' : ''} onClick={() => setScreenAvailabilityFilter(AvailabilityType.deactivated)}>Deactivated</li>
            </ul>
          </div>
          {!mobile && screens.length > 0 &&
            <Input type="search" prefix={<SearchOutlined />} placeholder="Search Screens" value={search}
              onChange={ev => onSearchChange(ev.target.value)} />}
        </header>}

      {isProSubscription(member) && totalScreensAndLicenses > 0 && <div className={"tb-head"}>
        <span className="status-col"></span>
        <span className="location-col" onClick={() => onLocSortClick()}>
          Location
          <SortScreenIcon ascending={sortType === SortType.locNameAscending} descending={sortType === SortType.locNameDescending} />
        </span>
        <span className="screen-col" onClick={() => onScreenNameSortClick()}>
          {!mobile && <>
            Screen
            <SortScreenIcon ascending={sortType === SortType.screenNameAscending} descending={sortType === SortType.screenNameDescending} />
          </>}
        </span>
        <span className="content-col" onClick={() => onContentTypeSortClick()}>
          Assigned Content
          <SortScreenIcon ascending={sortType === SortType.contentTypeAscending} descending={sortType === SortType.contentTypeDescending} />
        </span>
        <span className="hdmi-col">HDMI</span>
        <span className="wifi-col">Wi-Fi</span>
        {!member.owner && <span className="locked-col">Locked</span>}
        <span className="menu-col">
          <div style={{ width: "30px", display: "flex" }}>
            {mobile && <>
              <div className="sort-btn" onClick={() => setMobileSortMenuVisible(!isMobileSortMenuVisible)}>
                <img src="https://timber.mhmcdn.com/site/marketing/icons/Sort.svg" alt="sort" />
              </div>
              <Drawer
                placement={"bottom"}
                visible={isMobileSortMenuVisible}
                onClose={() => setMobileSortMenuVisible(false)}
                closeIcon={<DownOutlined />}
                className={"mobile-bottom-menu sort-menu"}
                height={350}>
                <div className="mobile-menu-header">
                  Sort by
                </div>
                <ul>
                  <li onClick={() => onSortMobile(SortType.locNameAscending)}>
                    <ArrowUpOutlined />
                    Location (Alphabetical A-Z)
                  </li>
                  <li onClick={() => onSortMobile(SortType.locNameDescending)}>
                    <ArrowDownOutlined />
                    Location (Alphabetical Z-A)
                  </li>
                  <div className="separator" />
                  <li onClick={() => onSortMobile(SortType.screenNameAscending)}>
                    <ArrowUpOutlined />
                    Screen Name (Alphabetical A-Z)
                  </li>
                  <li onClick={() => onSortMobile(SortType.screenNameDescending)}>
                    <ArrowDownOutlined />
                    Screen Name (Alphabetical Z-A)
                  </li>
                </ul>
              </Drawer>
            </>}

            {mobile && <>
              <div className="filter-btn" onClick={() => setMobileFilterMenuVisible(!isMobileFilterMenuVisibile)}>
                <FilterOutlined />
                {numFilters > 0 && <div className="num-filters">{numFilters}</div>}
              </div>
              <Drawer
                placement={"bottom"}
                visible={isMobileFilterMenuVisibile}
                onClose={() => setMobileFilterMenuVisible(false)}
                closeIcon={<DownOutlined />}
                className={"mobile-bottom-menu filter-menu"}
                height={500}>
                <div className="mobile-menu-header">
                  Filter
                </div>
                {filterMenuList}
                {filterFooter}
              </Drawer>
            </>}

            {!mobile && <Dropdown
              placement="bottomLeft"
              visible={isFilterMenuVisible}
              overlayClassName="filter-overlay"
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              overlay={<div ref={filterMenuRef}>
                {filterMenuList}
                {filterFooter}
              </div>}
            >
              {withTooltip('Filter', <div className="filter-btn" onClick={() => setFilterMenuVisible(!isFilterMenuVisible)}>
                <FilterOutlined />
                {numFilters > 0 && <div className="num-filters">{numFilters}</div>}
              </div>)}
            </Dropdown>}
          </div>
        </span>
      </div>}

      <div className={"screens"}>
        {(!isProSubscription(member) || totalScreensAndLicenses === 0) &&
          <div className={"no-data promo"}>
            <div className="video-container">
              <video src="https://timber.mhmcdn.com/site/marketing/homepage/dashboard/DSAGeometricFull.mp4" autoPlay loop muted playsInline />
            </div>
            <div className="scroll">
              <div className="text">
                <div className="icon-img">
                  <img src="https://timber.mhmcdn.com/site/marketing/icons/DSABlack.svg" alt="DSA" />
                </div>
                <h2>Simply integrated digital signage!</h2>
                <h3>Manage your digital menu boards across all your restaurant locations <br />with the MustHaveMenus Display App and media player.</h3>
                <div className="dsa-points">
                  <div className="point">
                    <img src="https://timber.mhmcdn.com/site/marketing/icons/CheckmarkRed.svg" alt="check" />
                    <span>POS data updates</span>
                  </div>
                  <div className="point">
                    <img src="https://timber.mhmcdn.com/site/marketing/icons/CheckmarkRed.svg" alt="check" />
                    <span>Dashboard view</span>
                  </div>
                  <div className="point">
                    <img src="https://timber.mhmcdn.com/site/marketing/icons/CheckmarkRed.svg" alt="check" />
                    <span>Menu dayparting</span>
                  </div>
                  <div className="point">
                    <img src="https://timber.mhmcdn.com/site/marketing/icons/CheckmarkRed.svg" alt="check" />
                    <span>Playlists and more!</span>
                  </div>
                </div>
                {isProSubscription(member) && <Button type={"primary"} size={"large"} onClick={() => onOrderDevice()}>Order Now</Button>}
                {!isProSubscription(member) && <Button type={"primary"} size={"large"} onClick={() => onUpgradeClick()}>Upgrade to Pro</Button>}
                <div className="learn-more">
                  <a href="/feature/book-demo-auth.html" target="_blank" rel="noreferrer">Book a Demo</a>
                  &nbsp;&nbsp;|&nbsp;&nbsp;
                  <a href="/feature/digital-menu-board-solution.html" target="_blank" rel="noreferrer">Learn More</a>
                </div>
              </div>
              {!mobile &&
                <div className="testimonials">
                  <div className="t-card">
                    <div className="img-quote">
                      <img src="https://timber.mhmcdn.com/site/feature/dsa-feature/handlebar-logo.webp" alt="Handlebar Diner" />
                      <p className="quote">"It's a game changer!"</p>
                    </div>
                    <div className="attribution">
                      <div className="name">Kevin</div>
                      <div className="biz">Handlebar Diner</div>
                    </div>
                  </div>
                  <div className="t-card">
                    <div className="img-quote">
                      <img src="https://timber.mhmcdn.com/site/feature/dsa-feature/JohnnyLogo.webp" alt="Handlebar Diner" />
                      <p className="quote">"I love how simple it is!"</p>
                    </div>
                    <div className="attribution">
                      <div className="name">Noura</div>
                      <div className="biz">Johnny Mañana's</div>
                    </div>
                  </div>
                  <div className="t-card">
                    <div className="img-quote">
                      <img className="lebos" src="https://timber.mhmcdn.com/site/feature/dsa-feature/LebosLogo.webp" alt="Handlebar Diner" />
                      <p className="quote">"This will speed up and simplify our message delivery."</p>
                    </div>
                    <div className="attribution">
                      <div className="name">David</div>
                      <div className="biz">Lebo's Bar and Grill</div>
                    </div>
                  </div>
                </div>}
              {mobile &&
                <Carousel autoplay autoplaySpeed={3000} dots={false}>
                  <div>
                    <div className="t-card">
                      <div className="img-quote">
                        <img src="https://timber.mhmcdn.com/site/feature/dsa-feature/handlebar-logo.webp" alt="Handlebar Diner" />
                        <p className="quote">"It's a game changer!"</p>
                      </div>
                      <div className="attribution">
                        <div className="name">Kevin</div>
                        <div className="biz">Handlebar Diner</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="t-card">
                      <div className="img-quote">
                        <img src="https://timber.mhmcdn.com/site/feature/dsa-feature/JohnnyLogo.webp" alt="Handlebar Diner" />
                        <p className="quote">"I love how simple it is!"</p>
                      </div>
                      <div className="attribution">
                        <div className="name">Noura</div>
                        <div className="biz">Johnny Mañana's</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="t-card">
                      <div className="img-quote">
                        <img className="lebos" src="https://timber.mhmcdn.com/site/feature/dsa-feature/LebosLogo.webp" alt="Handlebar Diner" />
                        <p className="quote">"This will speed up and simplify our message delivery."</p>
                      </div>
                      <div className="attribution">
                        <div className="name">David</div>
                        <div className="biz">Lebo's Bar and Grill</div>
                      </div>
                    </div>
                  </div>
                </Carousel>}
            </div>
          </div>
        }

        {isProSubscription(member) && totalScreensAndLicenses > 0 &&
          <ul>
            {!!deactivatedDevices && !search && (numFilters === 0 || filteredStatuses.includes('deactivated')) && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.deactivated) && Array.from(Array(deactivatedDevices).keys()).map(it => {
              return <li className="screen" key={it}>
                <span className="status-col">
                  <span className={"status-indicator deactivated"}>deactivated</span>
                </span>
                <span className="location-col">{doubleDash}</span>
                <span className="screen-col">
                  {!mobile && <div className="playpause-btn offline">
                    <div className="icon"><StartPlayingIcon /></div>
                  </div>}
                  <div className="thumbnail-container no-content">Deactivated<br />Screen {it + 1}</div>
                  &nbsp;&nbsp;&nbsp;<i>Screen Name</i>
                  {mobile && <Button className={"reactivate"} disabled={reactivating} onClick={() => reactivateScreenLicense()}>Reactivate License</Button>}
                </span>
                <span className="content-col"><Button className={"reactivate"} disabled={reactivating} onClick={() => reactivateScreenLicense()}>Reactivate License</Button></span>
                <span className="hdmi-col"><CloseCircle /></span>
                <span className="wifi-col">
                  <div>
                    <span><WiFiSignalOff /></span>
                    <span>WiFi Unavailable</span>
                  </div>
                </span>
                {!member.owner && <span className="locked-col">
                </span>}
                {!mobile && <span className="menu-col"><div style={{ width: "30px" }}></div></span>}
              </li>
            })}
            {!!availableDevices && !search && (numFilters === 0 || filteredStatuses.includes('offline')) && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.new) && Array.from(Array(availableDevices).keys()).map(it => {
              return <AvailableScreenCard
                it={it}
                deactivateScreen={deactivateScreen}
                setSetupModal={setSetUpModal}
                withTooltip={withTooltip}
              />
            })}
            {search && screens.length > 0 && screens.filter(it => it.visible).length === 0 && <div className="no-matches">
              <span>No matches found.</span>
            </div>}
            {screens.length > 0 && (screenAvailabilityFilter === AvailabilityType.all || screenAvailabilityFilter === AvailabilityType.active) && screens
              .filter(it => it.visible)
              .map(it => <li className={"screen"} key={it.serial}>
                {(!loadingContent || active?.serial !== it.serial) && <ScreenCard
                  it={it}
                  screens={screens}
                  setScreens={setScreens}
                  boards={boards}
                  playlists={playlists}
                  schedules={schedules}
                  uploads={uploads}
                  lockedForUser={lockedForUser(it)}
                  setActive={setActive}
                  setEditContentModal={setEditContentModal}
                  openPreviewModal={openPreviewModal}
                  openManageScreenModal={openManageScreenModal}
                  rebootScreen={rebootScreen}
                  showDiagnostics={showDiagnostics}
                  removeScreen={removeScreen}
                  withTooltip={withTooltip}
                />}
                {(loadingContent && active?.serial === it.serial) && <Spinner />}
              </li>)}
          </ul>}
      </div>

      <ScreenSetUpModal
        isVisible={isSetUpModal}
        onClose={() => setSetUpModal(false)}
        onSaved={onScreenAdded}
        seeHelp={() => setSetUpHelpModal(true)}
        withTooltip={withTooltip}
      />
      <ScreenSetUpHelpModal
        isVisible={isSetUpHelpModal}
        onClose={() => setSetUpHelpModal(false)}
      />

      <ManageScreenModal
        isVisible={isManageScreenModal}
        onClose={onCloseManageScreenModal}
        onSaved={onScreenUpdated}
        currentScreen={active!}
        withTooltip={withTooltip}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <EditScreenContentModal
        id={active?.serial}
        screen={active}
        locationId={location.internalId}
        isVisible={isEditContentModal}
        withTooltip={withTooltip}
        onDesignSelect={onDesignSelect}
        onPlaylistSelect={onPlaylistSelect}
        onScheduleSelect={onScheduleSelect}
        onUploadSelect={onUploadSelect}
        onClose={() => setEditContentModal(false)}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <PreviewModal
        previewType={previewType}
        previewBoard={previewBoard}
        previewEntries={previewEntries}
        previewUploadId={previewUploadId}
        isVisible={isPreviewModal}
        onClose={() => setPreviewModal(false)}
        openMobileMenu={() => triggerOpenMenu(active!)}
      />

      <RebootScreenModal
        isVisible={isRebootScreenModal}
        onClose={onCloseRebootScreenModal}
        active={active!}
      />

      <DisplayDiagnosticsOnScreenConfirmationModal
        isVisible={isDiagnosticsScreenModal}
        onClose={onCloseDiagnosticsScreenModal}
        active={active!}
      />

      <RemoveScreenModal
        isVisible={isRemoveScreenModal}
        onClose={onCloseRemoveScreenModal}
        active={active!}
      />

      <DeactivateScreenModal
        it={activeNumber}
        isVisible={isDeactivateScreenModal}
        onClose={onCloseDeactivateScreenModal}
      />
    </section>}
  </div>
}
